import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye } from 'lucide-react';
import { INTAKE_TEXT } from './PublicCandidateForm';
import { IntakeCustomBlock } from './IntakeCustomBlock';
import { LinkContext } from './shared/LinkText';
import { openTemplateFile } from './shared/LinkTextInput';

const SECTION_KEY = {
  personal: ['secPersonal'], emergency: ['secEmergency'], business: ['secBusiness', 'businessHint'],
  certs: ['secCerts', 'certsHint'], visas: ['secVisas'], documents: ['secFiles'], notes: ['secNotes'],
};
const PREVIEW_CTX = { openFile: openTemplateFile };
const txt = (key, lang) => (INTAKE_TEXT[key] || [key, key])[lang === 'pl' ? 1 : 0].replace('{company}', '');

const PreviewSection = ({ title, hint, children, testId }) => (
  <section className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid={testId}>
    <div>
      <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      {hint && <p className="text-xs text-slate-500 mt-0.5">{hint}</p>}
    </div>
    {children}
  </section>
);

const FakeInput = ({ label }) => (
  <div>
    <p className="text-xs font-medium text-slate-700 mb-1">{label}</p>
    <div className="h-9 rounded-md border border-slate-200 bg-slate-50" />
  </div>
);

/** What the applicant will see, in English or Polish, updated as the template is edited. */
export const IntakeFormPreview = ({ blocks = [] }) => {
  const { t } = useTranslation();
  const [lang, setLang] = useState('en');
  const [answers, setAnswers] = useState({});
  return (
    <LinkContext.Provider value={PREVIEW_CTX}>
    <div className="rounded-xl bg-slate-100 p-3 space-y-3" data-testid="tpl-preview">
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
          <Eye className="h-3.5 w-3.5" />{t('intakeTemplates.preview')}
        </p>
        <div className="inline-flex rounded-md border border-slate-300 bg-white p-0.5 text-xs">
          {['en', 'pl'].map((l) => (
            <button key={l} type="button" onClick={() => setLang(l)} data-testid={`tpl-preview-lang-${l}`}
              className={`rounded px-2 py-0.5 transition-colors ${lang === l ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
              {l.toUpperCase()}
            </button>
          ))}
        </div>
      </div>
      <PreviewSection title={txt('secContact', lang)} hint={txt('contactHint', lang)} testId="tpl-preview-contact">
        <div className="grid grid-cols-2 gap-2">
          {['firstName', 'lastName', 'phone', 'email'].map((k) => <FakeInput key={k} label={`${txt(k, lang)} *`} />)}
        </div>
      </PreviewSection>
      {blocks.map((b) => {
        if (b.type === 'builtin') {
          const [title, hint] = SECTION_KEY[b.key] || [b.key];
          return (
            <PreviewSection key={b.id} title={txt(title, lang)} hint={hint && txt(hint, lang)} testId={`tpl-preview-builtin-${b.key}`}>
              <p className="text-xs italic text-slate-400">
                {lang === 'pl' ? 'Tutaj pojawią się standardowe pytania z wbudowanego formularza.' : 'Standard questions from the built-in form appear here.'}
              </p>
            </PreviewSection>
          );
        }
        return (
          <div key={b.id} className={b.type === 'heading' || b.type === 'caption' ? '' : 'rounded-xl border border-slate-200 bg-white p-4'}
            data-testid={`tpl-preview-block-${b.id}`}>
            <IntakeCustomBlock block={b} lang={lang} value={answers[b.id]}
              onChange={(v) => setAnswers((a) => ({ ...a, [b.id]: v }))} />
          </div>
        );
      })}
    </div>
    </LinkContext.Provider>
  );
};

export default IntakeFormPreview;
